import * as React from "react";
import { createPortal } from "./browserFenster/fensterPortal";
import { Rnd } from "react-rnd";
import {
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from "@fluentui/react-components";
import {
  Dismiss20Regular,
  Subtract20Regular,
  Square20Regular,
  SquareMultiple20Regular,
  WindowNew20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../risikoanalyseStep/tokens";
import { useWindowManager, type ManagedWindow } from "./WindowManagerContext";
import { useResponsive } from "../../hooks/useResponsive";
import WindowConfirmDialog from "./WindowConfirmDialog";
import { Z_LAYERS } from "./zLayers";
import { fitToViewport } from "./centerPosition";
import { abdockPosition, andockGeometrie, andockSeiteFuer, type AndockSeite } from "./andocken";
import { useBrowserFenster } from "./browserFenster/useBrowserFenster";
import { BrowserFensterHuelle } from "./browserFenster/BrowserFensterHuelle";
import { BrowserFensterKopf } from "./browserFenster/BrowserFensterKopf";
import { alleFenster } from "./browserFenster/fensterRegister";

const useStyles = makeStyles({
  shell: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    boxShadow: "0 16px 48px rgba(45,20,43,0.18), 0 2px 6px rgba(45,20,43,0.06)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    pointerEvents: "auto",
  },
  shellMaximized: {
    // Maximize: nahtloser Vollbild-Look — keine Card-Border/Radius/Shadow,
    // damit die eingebettete Page wie eine normale Seite wirkt.
    ...shorthands.border("0"),
    ...shorthands.borderRadius("0"),
    boxShadow: "none",
  },
  titleBar: {
    cursor: "move",
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "6px",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    flexShrink: 0,
    userSelect: "none",
    // Touch-Geräte (viele Windows-Notebooks haben einen Touchscreen): ohne
    // `touch-action: none` deutet der Browser das Wischen auf der Titelleiste
    // als Scroll-Geste, schickt mitten im Ziehen ein `pointercancel` und der
    // Drag reißt ab. react-draggable allein verhindert das nicht zuverlässig.
    touchAction: "none",
  },
  titleBarHell: {
    // Derselbe Ton wie die Kopfzeile der PDF-Leiste (`--aub`, `.pdfx-schnell`
    // in documents/pdf-editor/pdfEditor.css) — siehe `titelleisteHell`.
    backgroundColor: "#7d2169",
  },
  titleBarMaximized: {
    // Im Maximize-Modus etwas kompakter, damit die Titelleiste weniger
    // dominant über der eingebetteten Page sitzt — Controls bleiben aber
    // sichtbar (kein versteckter Floating-Pill mehr).
    paddingTop: "6px",
    paddingBottom: "6px",
    cursor: "default",
  },
  titleIcon: {
    display: "inline-flex",
    alignItems: "center",
    color: BRAND.gold,
  },
  headerLeft: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    marginRight: "4px",
  },
  titleText: {
    flexGrow: 1,
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 500,
    color: BRAND.white,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actions: {
    display: "inline-flex",
    columnGap: "2px",
    flexShrink: 0,
  },
  actionBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: BRAND.white,
    // Touch-Target: Desktop kompakt-aber-bequem (32×32), auf Phone ≥44 px
    // (siehe actionBtnPhone) für sichere Treffbarkeit mit dem Finger.
    width: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    ...shorthands.border("0"),
    ...shorthands.borderRadius("6px"),
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.12)",
      color: BRAND.gold,
    },
  },
  actionBtnPhone: {
    // Phone: vergrößerte Touch-Targets (≥44 px, wie NavigationLayout/Dock).
    width: "44px",
    height: "44px",
  },
  closeBtn: {
    ":hover": {
      backgroundColor: "#a1331c",
      color: BRAND.white,
    },
  },
  body: {
    flexGrow: 1,
    overflow: "auto",
    backgroundColor: BRAND.bg,
  },
});

type Props = {
  window: ManagedWindow;
  /**
   * Phone-Modus: kein schwebendes/ziehbares Fenster, sondern Vollbild-Panel
   * über die volle Content-Fläche. Drag/Resize und der „Maximieren"-Button
   * entfallen (immer schon voll); Minimieren bleibt erlaubt.
   */
  fullscreen?: boolean;
};

/** Was nach dem Schließen passiert: Browserfenster zu, oder dort KMVS zeigen. */
type SchliessenDanach = "zu" | "kmvs";

// Safe-Zone-Policy: das Fenster darf in den Rand "verschwinden", aber so weit,
// dass der User die Titelleiste immer noch greifen kann.
const MIN_VISIBLE_X = 80;
const TITLE_BAR_VISIBLE = 40;
const clampToSafeZone = (
  pos: { x: number; y: number },
  size: { width: number; height: number },
  node: HTMLElement | null,
): { x: number; y: number } => {
  const parent = node?.parentElement;
  if (!parent) return pos;
  const lw = parent.clientWidth;
  const lh = parent.clientHeight;
  const minX = -size.width + MIN_VISIBLE_X;
  const maxX = lw - MIN_VISIBLE_X;
  const minY = 0;
  const maxY = Math.max(0, lh - TITLE_BAR_VISIBLE);
  return {
    x: Math.min(Math.max(pos.x, minX), maxX),
    y: Math.min(Math.max(pos.y, minY), maxY),
  };
};

/**
 * Cursor des Abfang-Blattes je Ziehrichtung. Während des Ziehens liegt das
 * Blatt über dem Resize-Griff, also muss es dessen Cursor selbst zeigen —
 * sonst springt der Mauszeiger mitten in der Geste auf den Standardpfeil.
 */
const RESIZE_CURSOR: Record<string, string> = {
  top: "ns-resize",
  bottom: "ns-resize",
  left: "ew-resize",
  right: "ew-resize",
  topLeft: "nwse-resize",
  bottomRight: "nwse-resize",
  topRight: "nesw-resize",
  bottomLeft: "nesw-resize",
};

/** Zeigerposition eines Maus- oder Touch-Ereignisses (Viewport-Koordinaten). */
const zeigerAus = (e: unknown): { x: number; y: number } | null => {
  const ev = e as Partial<MouseEvent> & Partial<TouchEvent>;
  const t = ev.changedTouches?.[0] ?? ev.touches?.[0];
  if (t) return { x: t.clientX, y: t.clientY };
  if (typeof ev.clientX === "number" && typeof ev.clientY === "number") {
    return { x: ev.clientX, y: ev.clientY };
  }
  return null;
};

/** Unter so viel Bewegung (px) ist ein Griff an die Titelleiste ein Klick, kein Ziehen. */
const ZIEH_SCHWELLE = 4;

const FloatingWindow: React.FC<Props> = ({ window: w, fullscreen = false }) => {
  const styles = useStyles();
  const { isPhone } = useResponsive();
  const {
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
    focusWindow,
    moveWindow,
    resizeWindow,
    dockWindow,
    requestCloseWindow,
    hasWindowCloseGuard,
    setWindowBrowserFenster,
  } = useWindowManager();

  // Touch-Targets: auf Phone die Chrome-Buttons (Min/Max/Schließen) auf ≥44 px
  // anheben; Desktop bleibt mit der kompakten Basis-Größe (32 px).
  const actionBtnClass = mergeClasses(
    styles.actionBtn,
    isPhone && styles.actionBtnPhone,
  );

  // Vollseite: wie maximiert (volle Fläche, nahtlose Optik), aber OHNE
  // Titelleiste — bedient wird sie über die Knöpfe der Seite selbst
  // (PageModeControls).
  const isPage = w.state === "page";
  // Auf Phone ist jedes Fenster vollflächig — die Maximize-Optik (keine
  // Card-Border/Radius/Shadow) wird wiederverwendet.
  const isMax = w.state === "maximized" || isPage || fullscreen;
  // Ausgelagert (eigenes Browserfenster): im Hauptfenster wie minimiert.
  const isMin = w.state === "minimized" || !!w.browserFenster;

  // Minimierte Fenster werden NICHT unmountet — sonst geht der React-State
  // (Form-Inputs in NewRiskPage / *ContractCreatePage) verloren. Stattdessen
  // via display:none aus dem Layout nehmen; pointer-events ist dann eh tot.
  const handleFocus = () => focusWindow(w.id);

  /**
   * Cursor-Form, solange gezogen wird — `null` heißt „gerade nicht".
   *
   * Warum das nötig ist: `react-draggable` hängt seine `mousemove`/`mouseup`
   * an das Dokument der Seite. Ein `<iframe>` hat ein EIGENES Dokument —
   * wandert der Zeiger im Ziehen darüber (die Mail-Vorschau im Schriftverkehr
   * ist genau so eins), bekommt die Seite keine Bewegung mehr mit: das Fenster
   * bleibt stehen, und das `mouseup` über dem iframe beendet die Geste nie.
   * Danach klebt das Fenster am Zeiger und die Knöpfe — auch „Minimieren" —
   * reagieren nicht mehr.
   *
   * Auf einem großen Bildschirm zieht man oft an der Vorschau vorbei und merkt
   * nichts; auf kleineren (Windows-Notebooks, dazu Skalierung und Rollbalken,
   * die Platz kosten) führt fast jeder Weg nach links oder rechts darüber.
   * Genau das ist der gemeldete Unterschied Mac ↔ Windows — kein Browser-Bug.
   */
  const [ziehCursor, setZiehCursor] = React.useState<string | null>(null);
  const ziehtGerade = ziehCursor !== null;
  /** Wo der Zeiger in der laufenden Geste zuletzt war (Viewport-Koordinaten). */
  const zeigerRef = React.useRef<{ x: number; y: number } | null>(null);
  /** Zeiger zu Beginn des Ziehens — trennt Klick von Ziehen. */
  const ziehStartRef = React.useRef<{ x: number; y: number } | null>(null);
  /** Rand, an dem das Fenster beim Loslassen andocken würde (für die Vorschau). */
  const [andockZiel, setAndockZiel] = React.useState<AndockSeite | null>(null);
  const andockZielRef = React.useRef<AndockSeite | null>(null);
  const setzeAndockZiel = (ziel: AndockSeite | null) => {
    if (andockZielRef.current === ziel) return;
    andockZielRef.current = ziel;
    setAndockZiel(ziel);
  };

  // Sicherheitsnetz: Geht ein `mouseup` doch verloren (Kontextmenü, Alt-Tab,
  // Fokuswechsel), zöge react-draggable ewig weiter und das Blatt bliebe über
  // der Seite liegen — die ganze Oberfläche wäre tot. Sobald sich der Zeiger
  // ohne gedrückte Taste bewegt, wird deshalb ein `mouseup` nachgereicht:
  // react-draggable beendet die Geste dann selbst und meldet sie regulär über
  // `onDragStop`/`onResizeStop` zurück.
  //
  // Das nachgereichte `mouseup` trägt die LETZTE Zeigerposition der Geste.
  // react-draggable rechnet die Endlage aus den Koordinaten des `mouseup`; ohne
  // sie stand dort (0, 0), und das Fenster flog nach links oben in die Ecke
  // (User 26.10.2026: „wenn ich das Fenster manchmal irgendwo hinziehe, dann
  // verschiebt sich das komplett in die Ecke").
  React.useEffect(() => {
    if (!ziehtGerade) return;
    const beenden = () => {
      const z = zeigerRef.current;
      document.dispatchEvent(
        new MouseEvent("mouseup", { bubbles: true, clientX: z?.x ?? 0, clientY: z?.y ?? 0 }),
      );
      setZiehCursor(null);
    };
    const beiBewegung = (e: MouseEvent) => {
      if (e.buttons === 0) beenden();
      else zeigerRef.current = { x: e.clientX, y: e.clientY };
    };
    alleFenster.addEventListener("mousemove", beiBewegung);
    window.addEventListener("blur", beenden);
    alleFenster.addEventListener("pointercancel", beenden);
    return () => {
      alleFenster.removeEventListener("mousemove", beiBewegung);
      window.removeEventListener("blur", beenden);
      alleFenster.removeEventListener("pointercancel", beenden);
    };
  }, [ziehtGerade]);

  // Schließen mit Bestätigung, wenn das Fenster ungespeicherte Eingaben hat
  // (w.dirty wird von eingebetteten Formularen via onDirtyChange gesetzt).
  // Statt nativem confirm: der schlanke kvp-rd-Dialog mit Verwerfen/Minimieren.
  //
  // `danach`: „Zurück in KMVS“ im eigenen Browserfenster (User 27.10.2026) ist
  // dasselbe Schließen — nur geht das Browserfenster danach nicht zu, sondern
  // zeigt KMVS (siehe useBrowserFenster, `kmvsZeigenRef`).
  const kmvsZeigenRef = React.useRef(false);
  const [confirmOpen, setConfirmOpen] = React.useState<SchliessenDanach | null>(null);
  const handleRequestClose = React.useCallback(
    (danach: SchliessenDanach = "zu") => {
      // Fragt der Inhalt selbst (Kontaktakte: „Speichern | Verwerfen |
      // Abbrechen"), hat seine Rückfrage Vorrang vor der allgemeinen.
      if (hasWindowCloseGuard?.(w.id)) {
        kmvsZeigenRef.current = danach === "kmvs";
        void requestCloseWindow(w.id).then((zu) => {
          if (!zu) kmvsZeigenRef.current = false;
        });
        return;
      }
      if (w.dirty) {
        setConfirmOpen(danach);
        return;
      }
      kmvsZeigenRef.current = danach === "kmvs";
      closeWindow(w.id);
    },
    [w.dirty, w.id, closeWindow, hasWindowCloseGuard, requestCloseWindow],
  );

  const closeConfirm = (
    <WindowConfirmDialog
      open={confirmOpen !== null}
      title="Ungespeicherte Eingaben"
      message="Dieses Fenster enthält ungespeicherte Eingaben. Verwerfen, oder für später minimieren?"
      onDiscard={() => {
        kmvsZeigenRef.current = confirmOpen === "kmvs";
        setConfirmOpen(null);
        closeWindow(w.id);
      }}
      onMinimize={() => {
        // Aus dem Browserfenster heraus: minimiert zurück ins Hauptfenster.
        kmvsZeigenRef.current = confirmOpen === "kmvs";
        setConfirmOpen(null);
        setWindowBrowserFenster(w.id, false);
        minimizeWindow(w.id);
      }}
      onCancel={() => setConfirmOpen(null)}
    />
  );

  // Eigenes Browserfenster (User 26.10.2026). Der Inhalt hängt in einem
  // festen Container, den der Hook zwischen Körper und Browserfenster umhängt.
  const [koerperEl, setKoerperEl] = React.useState<HTMLDivElement | null>(null);
  const { inhaltEl, eingehaengt, offen } = useBrowserFenster(w, {
    koerperEl,
    layerEl: () => shellRef.current?.parentElement?.parentElement ?? null,
    schliessenAnfragen: handleRequestClose,
    kmvsZeigenRef,
  });
  const fensterDok = offen ? offen.popup.document : document;

  // Maximize: feste Position 0/0 und Full-Size. Der Parent-Container der
  // Window-Layer hat position: relative, sodass Rnd's `bounds="parent"`
  // greift und 100% innerhalb des Main-Content-Bereichs bleibt.
  // T-27: Wird der Layer kleiner als das Fenster (erstes Öffnen ohne
  // Messwert, Browserfenster verkleinert, Laptop statt großer Monitor), wird
  // das Fenster auf die verfügbare Fläche verkleinert und so verschoben, dass
  // seine Unterkante samt Fußzeile im sichtbaren Bereich liegt.
  const shellRef = React.useRef<HTMLDivElement | null>(null);
  const aktuellRef = React.useRef(w);
  aktuellRef.current = w;
  const passeAnLayerAn = React.useCallback(() => {
    const cur = aktuellRef.current;
    if (cur.state !== "normal") return;
    const layer = shellRef.current?.parentElement?.parentElement;
    if (!layer) return;
    const lw = layer.clientWidth;
    const lh = layer.clientHeight;
    if (lw <= 0 || lh <= 0) return;
    // Angedockt: bleibt die halbe Fläche, auch wenn sie sich ändert (Browser-
    // fenster, Menü ein-/ausgeklappt). Der Reducer übergeht, was schon passt.
    if (cur.angedockt) {
      dockWindow(cur.id, cur.angedockt, { width: lw, height: lh });
      return;
    }
    const rand = 12;
    const passt =
      cur.size.width <= lw - 2 * rand &&
      cur.size.height <= lh - 2 * rand &&
      cur.position.y + cur.size.height <= lh;
    if (passt) return;
    const neu = fitToViewport(cur.size, { width: lw, height: lh });
    const pos = {
      x: Math.max(rand, Math.min(cur.position.x, lw - neu.width - rand)),
      y: Math.max(rand, Math.min(cur.position.y, lh - neu.height - rand)),
    };
    if (
      neu.width === cur.size.width &&
      neu.height === cur.size.height &&
      pos.x === cur.position.x &&
      pos.y === cur.position.y
    ) {
      return;
    }
    resizeWindow(cur.id, neu, pos);
  }, [resizeWindow, dockWindow]);
  React.useLayoutEffect(() => {
    if (fullscreen) return;
    passeAnLayerAn();
    const layer = shellRef.current?.parentElement?.parentElement;
    if (!layer || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => passeAnLayerAn());
    ro.observe(layer);
    return () => ro.disconnect();
  }, [fullscreen, passeAnLayerAn, w.state]);

  const position = isMax ? { x: 0, y: 0 } : w.position;
  const size = isMax
    ? { width: "100%" as const, height: "100%" as const }
    : w.size;

  // Gemeinsamer Fenster-Inhalt (Titelleiste + Body). Auf Phone wird der
  // „Maximieren/Wiederherstellen"-Button ausgeblendet — das Fenster ist
  // immer voll; Minimieren bleibt (→ Wechsel über das Dock).
  const chrome = (
    <div
      ref={shellRef}
      className={mergeClasses(styles.shell, isMax && styles.shellMaximized)}
      style={{ width: "100%", height: "100%", position: "relative" }}
      // Für Tickets (tickets/ansichtErfassen.ts): welches Fenster liegt beim
      // Melden oben? Titel + Stapelhöhe; minimierte Fenster sind unsichtbar.
      data-fenster-titel={w.title}
      data-fenster-z={w.zIndex}
    >
      {/* Im Vollseiten-Modus entfällt die Titelleiste — sie MUSS aber als
          konditionaler Zweig an genau dieser JSX-Position stehen bleiben.
          Würde sie aus dem Kinder-Array entfernt, rutschte der Body von Index 1
          auf 0 und React remountet den kompletten Formularbaum (= Datenverlust
          genau beim Umschalten). */}
      {!isPage && !(fullscreen && isPhone && w.ohneTitelleisteAufHandy) && (
      <div
        className={mergeClasses(
          styles.titleBar,
          w.titelleisteHell && styles.titleBarHell,
          isMax ? styles.titleBarMaximized : " floating-window-drag",
        )}
      >
        {w.headerLeftSlot && (
          <span className={styles.headerLeft}>{w.headerLeftSlot}</span>
        )}
        {w.icon && <span className={styles.titleIcon}>{w.icon}</span>}
        <span className={styles.titleText}>{w.title}</span>
        {/* `floating-window-nodrag` nimmt den Button-Bereich vom Rnd-Drag aus
            (siehe `cancel`-Prop unten). Sonst ruft react-draggable auf Touch-
            Geräten beim `touchstart` der Titelleiste `preventDefault()` auf,
            was den synthetischen `click` unterdrückt → die Buttons reagieren
            auf Tablets (Querformat = Rnd-Pfad) nicht auf Fingertipp. */}
        <span className={mergeClasses(styles.actions, "floating-window-nodrag")}>
          {/* Vor Minimieren/Maximieren/Schließen, damit deren gewohnte Reihenfolge bleibt. */}
          {!fullscreen && (
            <button
              type="button"
              aria-label="Als eigenes Browserfenster öffnen"
              title="Als eigenes Browserfenster öffnen — z. B. für den zweiten Bildschirm"
              className={actionBtnClass}
              onClick={() => setWindowBrowserFenster(w.id, true)}
            >
              <WindowNew20Regular />
            </button>
          )}
          <button
            type="button"
            aria-label="Minimieren"
            className={actionBtnClass}
            onClick={() => minimizeWindow(w.id)}
          >
            <Subtract20Regular />
          </button>
          {!fullscreen &&
            (w.state === "maximized" ? (
              <button
                type="button"
                aria-label="Wiederherstellen"
                className={actionBtnClass}
                onClick={() => restoreWindow(w.id)}
              >
                <SquareMultiple20Regular />
              </button>
            ) : (
              <button
                type="button"
                aria-label="Maximieren"
                className={actionBtnClass}
                onClick={() => maximizeWindow(w.id)}
              >
                <Square20Regular />
              </button>
            ))}
          <button
            type="button"
            aria-label="Schließen"
            className={mergeClasses(actionBtnClass, styles.closeBtn)}
            onClick={() => handleRequestClose()}
          >
            <Dismiss20Regular />
          </button>
        </span>
      </div>
      )}
      {/* Der Inhalt steht im DOM nicht direkt hier, sondern in `inhaltEl`
          (siehe useBrowserFenster) — so kann er ins Browserfenster umziehen,
          ohne neu aufgebaut zu werden. Im React-Baum bleibt das Portal hier
          stehen: Ein Klick in den Inhalt erreicht so weiter `onMouseDown` am
          Rahmen und holt das Fenster nach vorn. */}
      <div className={styles.body} ref={setKoerperEl}>
        {eingehaengt &&
          createPortal(<BrowserFensterHuelle dok={fensterDok}>{w.render()}</BrowserFensterHuelle>, inhaltEl)}
      </div>
    </div>
  );
  const kopfImBrowserfenster =
    offen &&
    createPortal(
      <BrowserFensterHuelle dok={offen.popup.document}>
        <BrowserFensterKopf
          fensterId={w.id}
          titel={w.title}
          icon={w.icon}
          hell={w.titelleisteHell}
          onKmvsZeigen={() => handleRequestClose("kmvs")}
          onSchliessen={() => handleRequestClose()}
        />
      </BrowserFensterHuelle>,
      offen.geruest.kopf,
    );

  // Vorschau beim Ziehen an den Rand: die Fläche, die das Fenster beim
  // Loslassen einnimmt. Liegt unter dem gezogenen Fenster (gleiche Ebene, im
  // DOM davor) und nimmt keine Zeigerereignisse an.
  const flaecheEl = shellRef.current?.parentElement?.parentElement;
  const vorschau =
    ziehtGerade && andockZiel && flaecheEl
      ? andockGeometrie(andockZiel, { width: flaecheEl.clientWidth, height: flaecheEl.clientHeight })
      : null;

  // EIN Wurzelknoten für alle Viewports: auch der Phone-/Tablet-Vollbildfall
  // (fullscreen) läuft über <Rnd> — nur mit deaktiviertem Drag/Resize. Vorher
  // wechselte am 1024px-Breakpoint der Wurzel-Elementtyp (div ↔ Rnd); eine
  // iPad-Rotation remountete damit den kompletten Fensterinhalt und LÖSCHTE ein
  // halb ausgefülltes Formular. Volle Fläche + Position 0/0 liefert bereits
  // `isMax` (schließt fullscreen ein).
  return (
    <>
    {vorschau && (
      <div
        aria-hidden
        data-testid="fenster-andock-vorschau"
        style={{
          position: "absolute",
          left: vorschau.position.x + 6,
          top: vorschau.position.y + 6,
          width: Math.max(0, vorschau.size.width - 12),
          height: Math.max(0, vorschau.size.height - 12),
          zIndex: w.zIndex,
          pointerEvents: "none",
          borderRadius: 12,
          border: `2px solid ${BRAND.purple}`,
          background: "rgba(77, 32, 81, 0.10)",
          boxShadow: "0 8px 24px rgba(45,20,43,0.12)",
        }}
      />
    )}
    <Rnd
      position={position}
      size={size}
      // Vollflächig: keine Mindestmaße erzwingen — re-resizable schreibt
      // minWidth/minHeight in den Style und würde das Panel auf schmalen Phones
      // (< 420px) über den Viewport hinaus aufblasen.
      minWidth={isMax ? 0 : 420}
      minHeight={isMax ? 0 : 320}
      // Kein `bounds="parent"` — das Fenster darf während des Drags frei
      // über den Layer-Rand hinaus, wird vom Layer (overflow:hidden) optisch
      // abgeschnitten, und beim Loslassen via clampToSafeZone in den
      // Greif-Bereich zurückgezogen.
      disableDragging={isMax}
      enableResizing={!isMax}
      dragHandleClassName="floating-window-drag"
      // Buttons (Min/Max/Schließen) vom Drag ausnehmen — sonst frisst
      // react-draggables touchstart-preventDefault den Klick auf Touch-Geräten.
      cancel=".floating-window-nodrag"
      style={{
        zIndex: w.zIndex,
        pointerEvents: "auto",
        display: isMin ? "none" : undefined,
      }}
      onMouseDown={handleFocus}
      onDragStart={(e) => {
        setZiehCursor("move");
        zeigerRef.current = zeigerAus(e);
        ziehStartRef.current = zeigerRef.current;
      }}
      // Kein `return false` hier: react-draggable bräche die Geste sonst ab.
      onDrag={(e, d) => {
        const z = zeigerAus(e);
        if (!z) return;
        zeigerRef.current = z;
        const flaeche = (d.node as HTMLElement).parentElement?.getBoundingClientRect();
        setzeAndockZiel(flaeche ? andockSeiteFuer(z.x, flaeche) : null);
      }}
      onDragStop={(e, d) => {
        setZiehCursor(null);
        const ziel = andockZielRef.current;
        setzeAndockZiel(null);
        const start = ziehStartRef.current;
        ziehStartRef.current = null;
        const ende = zeigerAus(e) ?? zeigerRef.current;
        // Nur angeklickt, nicht gezogen: Ein angedocktes Fenster bleibt, wo es
        // ist — sonst löste schon ein Klick auf die Titelleiste es vom Rand.
        if (
          w.angedockt &&
          start &&
          ende &&
          Math.abs(ende.x - start.x) < ZIEH_SCHWELLE &&
          Math.abs(ende.y - start.y) < ZIEH_SCHWELLE
        ) {
          return;
        }
        const layer = (d.node as HTMLElement).parentElement;
        if (ziel && layer) {
          dockWindow(w.id, ziel, { width: layer.clientWidth, height: layer.clientHeight });
          return;
        }
        // Vom Rand weggezogen: zurück auf die Größe von vorher, der Zeiger
        // bleibt an derselben Stelle der Titelleiste.
        if (w.angedockt && w.vorAndocken && layer) {
          const links = layer.getBoundingClientRect().left;
          const pos = ende
            ? abdockPosition({
                zeigerX: ende.x - links,
                position: { x: d.x, y: d.y },
                breiteAngedockt: w.size.width,
                breiteVorher: w.vorAndocken.width,
              })
            : { x: d.x, y: d.y };
          resizeWindow(w.id, w.vorAndocken, clampToSafeZone(pos, w.vorAndocken, d.node as HTMLElement));
          return;
        }
        const clamped = clampToSafeZone(
          { x: d.x, y: d.y },
          w.size,
          d.node as HTMLElement,
        );
        moveWindow(w.id, clamped);
      }}
      onResizeStart={(e, dir) => {
        setZiehCursor(RESIZE_CURSOR[dir] ?? "move");
        zeigerRef.current = zeigerAus(e);
      }}
      onResizeStop={(_, __, ref, ___, pos) => {
        setZiehCursor(null);
        const newSize = {
          width: parseInt(ref.style.width, 10),
          height: parseInt(ref.style.height, 10),
        };
        const clamped = clampToSafeZone(pos, newSize, ref);
        resizeWindow(w.id, newSize, clamped);
      }}
    >
      {chrome}
    </Rnd>
      {/* Unsichtbares Blatt über der ganzen Seite, solange gezogen wird: Es
          hält den Zeiger von fremden Dokumenten (dem Mail-`<iframe>`) fern,
          ohne die Geste zu stören — die Ereignisse steigen von hier ganz
          normal zum Dokument auf, wo react-draggable zuhört. Nur während der
          Geste im Baum, danach sofort wieder weg. */}
      {ziehtGerade && (
        <div
          aria-hidden
          data-testid="fenster-zieh-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: Z_LAYERS.ziehOverlay,
            cursor: ziehCursor ?? "move",
          }}
        />
      )}
      {kopfImBrowserfenster}
      {/* Die Rückfrage erscheint dort, wo das Fenster gerade steht. */}
      {offen ? <BrowserFensterHuelle dok={fensterDok}>{closeConfirm}</BrowserFensterHuelle> : closeConfirm}
    </>
  );
};

export default FloatingWindow;
